// =========================================================================
// `useSeedNav()` — turning a `SeedNav` into something that happens.
//
// The demo carried navigation as JavaScript source inside its data:
//
//     "show('machine','L-33');setTimeout(()=>machineTab('record'),40)"
//     "audit('wos',{wo_type:'Preventive'})"
//     "openDoc('x:L-18_MXASSET.json')"
//     "ask('What is waiting in this module?')"
//
// `seed/modules.ts` and `seed/workflow-steps.ts` already parsed all of that
// into the {@link SeedNav} union at port time. This hook is the other end:
// one `dispatch` covering all four branches, used by the module rows, the
// view chips, the workflow cells, the note links, the route panels and the
// "also in the platform" chips — every clickable thing in this lane.
//
// Four branches, four destinations:
//
// | `kind`    | what happens                                              |
// |-----------|-----------------------------------------------------------|
// | `show`    | `routeForView()` → `navigate()`; unmapped pairs are inert  |
// | `audit`   | the `oem-source` drawer, with the filter applied to a slice|
// | `openDoc` | the lake document drawer, via `useLakeDoc()`               |
// | `ask`     | `askOem()` — the assistant, with the demo's own question   |
//
// `href` is exported beside `dispatch` so a `show` target can be rendered as
// a real link when the caller wants one: middle-click and "open in new tab"
// are worth having on a page that is mostly cross-references.
//
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrawer } from '@/app/providers';
import { askOem } from '@/versions/v1/shared/assistant/ask';
import { useLakeDoc } from '@/versions/v1/shared/lake-drawer';
import { routeForView } from '@/versions/v1/shared/oem-routes';
import type { AuditFilter, SeedNav } from '@/versions/v1/shared/seed/modules';

/** A row of any pack table, as the audit branch sees it. */
export type AuditRow = Readonly<Record<string, unknown>>;

/** Tables `audit(table, …)` can name, supplied by the calling scene. */
export type AuditTables = Readonly<Record<string, readonly AuditRow[]>>;

/** Does one row satisfy one clause of a demo `audit()` filter? */
function matches(row: AuditRow, clause: AuditFilter): boolean {
  const value = String(row[clause.field] ?? '');
  switch (clause.op) {
    case 'eq':
      return value === clause.value;
    case 'startsWith':
      return value.startsWith(clause.value);
    case 'matches':
      return new RegExp(clause.value).test(value);
  }
}

/**
 * The slice `audit()` opened, shaped for `SourceDrawerBody`.
 *
 * Capped at 200 rows. The demo showed the whole table because it was a table
 * in a page; a drawer that renders 3,920 work orders is a scroll, not an
 * audit. The cap is stated in `note` so the reader knows the list continues.
 */
export interface AuditSlice {
  readonly title: string;
  readonly kind: 'table';
  readonly table: string;
  readonly filter: string;
  readonly matched: number;
  readonly note?: string;
  readonly rows: readonly AuditRow[];
}

/** How a filter reads on screen: `wo_type = Preventive · status ^ Draft`. */
const describeFilter = (filter: readonly AuditFilter[]): string =>
  filter.length === 0
    ? 'no filter — the whole table'
    : filter
        .map((c) => `${c.field} ${c.op === 'eq' ? '=' : c.op === 'startsWith' ? '^' : '~'} ${c.value}`)
        .join(' · ');

/** Build the slice one `audit(table, filter)` call describes. */
export function auditSlice(
  table: string,
  filter: readonly AuditFilter[],
  tables: AuditTables,
): AuditSlice {
  const rows = tables[table] ?? [];
  const hits = rows.filter((row) => filter.every((clause) => matches(row, clause)));
  const CAP = 200;
  return {
    title: `${table} · ${hits.length} row${hits.length === 1 ? '' : 's'}`,
    kind: 'table',
    table,
    filter: describeFilter(filter),
    matched: hits.length,
    note:
      hits.length > CAP
        ? `Showing the first ${CAP} of ${hits.length} matching rows.`
        : rows.length === 0
          ? `The ${table} table is not loaded on this page.`
          : undefined,
    rows: hits.slice(0, CAP),
  };
}

/**
 * The demo's `openDoc` keys are `'<prefix>:<file>'`. The prefix names which of
 * the thirteen source folders the file came from, and the drawer prints it as
 * the document's provenance — the whole point of opening a source.
 */
const DOC_SOURCE: Readonly<Record<string, string>> = {
  x: 'Machine record export',
  doc: 'Document library',
  eml: 'Mailbox',
  mtg: 'Meeting record',
  vtt: 'Call transcript',
  sap: 'SAP PM export',
};

/** `'x:L-18_MXASSET.json'` → `['x', 'L-18_MXASSET.json']`. */
function splitDocKey(key: string): readonly [prefix: string, file: string] {
  const at = key.indexOf(':');
  return at === -1 ? ['', key] : [key.slice(0, at), key.slice(at + 1)];
}

/** What {@link useSeedNav} returns. */
export interface SeedNavApi {
  /** Run a seed navigation value. */
  readonly dispatch: (nav: SeedNav) => void;
  /** The route a `show` value goes to, or `null` for the other three kinds. */
  readonly href: (nav: SeedNav) => string | null;
}

/**
 * @param tables - pack tables the `audit` branch may slice. Pass what the
 *   scene has loaded; a table that is absent opens the drawer saying so,
 *   which is more honest than an empty list that looks like a real result.
 */
export function useSeedNav(tables: AuditTables = {}): SeedNavApi {
  const navigate = useNavigate();
  const { open: openDrawer } = useDrawer();
  const openLakeDoc = useLakeDoc();

  const href = useCallback(
    (nav: SeedNav): string | null =>
      nav.kind === 'show' ? routeForView(nav.action.view, nav.action.sub) : null,
    [],
  );

  const dispatch = useCallback(
    (nav: SeedNav) => {
      switch (nav.kind) {
        case 'show': {
          const route = routeForView(nav.action.view, nav.action.sub);
          // The demo's `setTimeout(() => machineTab('record'), 40)` tail was a
          // DOM-paint workaround. The machine page reads its tab from the
          // query string, so the intent travels as data instead.
          if (route) navigate(nav.machineTab ? `${route}?tab=${nav.machineTab}` : route);
          return;
        }
        case 'audit': {
          const slice = auditSlice(nav.table, nav.filter, tables);
          openDrawer('oem-source', {
            title: slice.title,
            kicker: 'Source',
            wide: true,
            data: slice as unknown as Record<string, unknown>,
          });
          return;
        }
        case 'openDoc': {
          const [prefix, file] = splitDocKey(nav.key);
          openLakeDoc({ file, connector: DOC_SOURCE[prefix] ?? 'Data store' });
          return;
        }
        case 'ask':
          askOem(nav.question);
      }
    },
    [navigate, openDrawer, openLakeDoc, tables],
  );

  return useMemo(() => ({ dispatch, href }), [dispatch, href]);
}
